import { Menu, Modal } from "antd";
import { useEffect, useState } from "react";
import { STORES } from "../../../utils";
import { Currency, GLAccountProps, Subsidiary } from "../constants";
import { CreateGLAccountButton } from "./CreateGLAccountButton";
import { CreateGLAccountInput } from "./CreateGLAccountInput";

enum AccountTypes {
  Bank = "bank",
  CreditCard = "credit_card",
}

function convertSnakeToWords(input: string): string {
  return input
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export const CreateGLAccountModal = ({
  store,
  visible,
  currencies,
  subsidaries,
  accountType,
  closeModal,
  createGLAccountHandler,
}: {
  store: STORES | null;
  visible: boolean;
  currencies: Currency[];
  subsidaries: Subsidiary[];
  accountType: string | null;
  closeModal: () => void;
  createGLAccountHandler: (
    props: GLAccountProps,
    successHandler: () => void,
    completeHandler: () => void,
  ) => void;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [selectedSubsidiary, setSelectedSubsidiary] =
    useState<Subsidiary | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<Currency | null>(
    null,
  );
  const [selectedProps, setSelectedProps] = useState<GLAccountProps>({
    account_type: accountType ?? AccountTypes.Bank,
  });
  const [currencyForced, setCurrencyForced] = useState(false);

  const setDefaultProps = () => {
    setSelectedCurrency(
      currencies.find((currency) => currency.currency_code === "USD") ??
        currencies[0],
    );
    setSelectedProps({
      account_type: accountType ?? AccountTypes.Bank,
      currency_code: selectedCurrency?.currency_code,
    });

    // Setting subsidiary will automatically force currency (if needed), so we don't need to call it again here.
    setSelectedSubsidiary(null);
  };

  const maybeForceCurrency = () => {
    const currentSubsidiary =
      selectedSubsidiary ?? subsidaries.find((x) => !x.parent_id);
    const matchingCurrency = currencies.find(
      (x) => x.currency_code === currentSubsidiary?.currency_code,
    );

    // True if currency must be set to a particular value.
    const targetCurrencyFound =
      accountType === AccountTypes.CreditCard &&
      !!matchingCurrency &&
      !!currentSubsidiary;
    setCurrencyForced(targetCurrencyFound);

    if (
      targetCurrencyFound &&
      selectedCurrency?.currency_code != currentSubsidiary.currency_code
    ) {
      // Credit card accounts must match the currency code of either the selected subsidiary,
      // or the root subsidiary (which is used if no subsidiary is explicitly selected).
      setSelectedCurrency(matchingCurrency);
    }
  };

  useEffect(() => {
    // Make sure we re-set account_type when the selected bank feed account changes.
    setDefaultProps();
  }, [accountType]);

  useEffect(() => {
    // If we are creating a credit_card account and the subsidiary changes, we need to make sure to update the currency to match the new subsidiary.
    maybeForceCurrency();
  }, [accountType, selectedSubsidiary]);

  // Update currency_code in input when new currency is selected.
  useEffect(() => {
    setSelectedProps({
      ...selectedProps,
      currency_code: selectedCurrency?.currency_code,
    });
  }, [selectedCurrency]);

  // Update subsidiary_id in input when new subsidiary is selected.
  useEffect(() => {
    setSelectedProps({
      ...selectedProps,
      subsidiary_id: selectedSubsidiary?.id,
    });
  }, [selectedSubsidiary]);

  const selectableCurrencies = (
    <Menu
      onClick={(e) => {
        const currency = currencies.find((x) => x.id === e.key);
        setSelectedCurrency(currency!);
      }}>
      {currencies.map((currency) => (
        <Menu.Item key={currency.id}>{getCurrencyName(currency)}</Menu.Item>
      ))}
    </Menu>
  );

  const selectableSubsidiaries = (
    <Menu
      onClick={(e) => {
        const subsidiary =
          e.key === "remove" ? null : subsidaries.find((x) => x.id === e.key);
        setSelectedSubsidiary(subsidiary!);
      }}>
      <Menu.Item key="remove">{"<No subsidiary>"}</Menu.Item>
      {subsidaries.map((subsidiary) => (
        <Menu.Item key={subsidiary.id}>{subsidiary.name}</Menu.Item>
      ))}
    </Menu>
  );

  const areRequiredFieldsSet = (): boolean => {
    return (
      !!selectedProps.account_type &&
      !!selectedProps.currency_code &&
      !!selectedProps.nominal_code &&
      !!selectedProps.name &&
      (store !== STORES.XERO ||
        !!selectedProps.additional_fields?.bank_account_number)
    );
  };

  return (
    <>
      <Modal
        visible={!!store && visible}
        footer={null}
        width={550}
        onCancel={closeModal}
        // Can only be closed explicitly.
        maskClosable={false}
        // Should appear in front of everything but error modal.
        zIndex={9000}
        afterClose={() => {
          // Always reset settings on close.
          setDefaultProps();
          setIsLoading(false);
        }}>
        <div className="pt-4 pr-12 pl-12">
          <div
            className="text-lg font-bold pb-2"
            style={{ textAlign: "center" }}>
            Add a new account
          </div>
          <div style={{ fontSize: 12, textAlign: "center" }}>
            Create a new general ledger account to map this bank activity. This
            will appear in your chart of accounts and can be edited later if
            needed.
          </div>
        </div>
        <div
          className="m-3"
          style={{
            flexWrap: "wrap",
            display: "flex",
            justifyContent: "space-between",
          }}>
          <CreateGLAccountInput
            id="input-name"
            label="Account name*"
            value={selectedProps.name}
            disabled={isLoading}
            placeholder="Total Checking"
            setValue={(value: string) => {
              setSelectedProps({ ...selectedProps, name: value });
            }}></CreateGLAccountInput>

          <CreateGLAccountInput
            id="input-code"
            label="Nominal code*"
            value={selectedProps.nominal_code}
            disabled={isLoading}
            placeholder="xxxx9471"
            setValue={(value: string) => {
              setSelectedProps({ ...selectedProps, nominal_code: value });
            }}></CreateGLAccountInput>

          <CreateGLAccountInput
            id="input-currency"
            label="Currency*"
            value={getCurrencyName(selectedCurrency)}
            // For credit_card accounts, the currency MUST match the selected subsidiary (or root).
            disabled={currencyForced || isLoading}
            infoText="This needs to match the currency code of the financial account."
            selectableItems={selectableCurrencies}></CreateGLAccountInput>

          {store === STORES.NETSUITE && (
            <CreateGLAccountInput
              id="input-subsidiary"
              label="Subsidiary"
              value={getSubsidiaryName(selectedSubsidiary)}
              fontSize={13}
              disabled={isLoading}
              selectableItems={selectableSubsidiaries}></CreateGLAccountInput>
          )}

          {store === STORES.XERO && (
            <CreateGLAccountInput
              id="input-number"
              label="Bank account number*"
              value={selectedProps.additional_fields?.bank_account_number}
              disabled={isLoading}
              placeholder="xxxx9471"
              setValue={(value: string) => {
                setSelectedProps({
                  ...selectedProps,
                  additional_fields: { bank_account_number: value },
                });
              }}></CreateGLAccountInput>
          )}

          {/* G/L account type has to match bank feed account type, so it will always be pre-selected and disabled. */}
          <CreateGLAccountInput
            id="input-type"
            label="Account type*"
            value={convertSnakeToWords(selectedProps.account_type)}
            infoText="This needs to match the account type of the financial account."></CreateGLAccountInput>
        </div>
        <CreateGLAccountButton
          text={"Save"}
          onClick={() => {
            setIsLoading(true);
            createGLAccountHandler(
              selectedProps,
              () => {
                // Only close modal on successful account creation.
                closeModal();
              },
              () => {
                setIsLoading(false);
              },
            );
          }}
          disabled={
            !areRequiredFieldsSet() || isLoading
          }></CreateGLAccountButton>
        <CreateGLAccountButton
          text={"Cancel"}
          inverseColors={true}
          onClick={closeModal}
          disabled={isLoading}></CreateGLAccountButton>
      </Modal>
    </>
  );
};

const getCurrencyName = (currency: Currency | null): string | undefined => {
  return currency
    ? `${currency.currency_code} - ${currency.currency_name}`
    : undefined;
};

const getSubsidiaryName = (subsidiary: Subsidiary | null): string => {
  return subsidiary ? subsidiary.name : "Select a subsidiary (if applicable)";
};
